import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  Compass,
  MapPin,
  Clock,
  Users,
  ArrowRight,
  Building2,
  Landmark,
  Briefcase,
  Sparkles,
  Mail,
  Leaf,
} from "lucide-react";
import { publicEndpoints } from "@/lib/api";
import { EspaceMenu } from "@/components/PublicChrome";
import heroImage1537 from "@/assets/hero-1537.webp";
import heroImage1200 from "@/assets/hero-1200.webp";
import heroImage800 from "@/assets/hero-800.webp";
import heroImageJpg from "@/assets/hero-1537.jpg";

/* -----------------------------------------------------------
   Landing — Thème "Aventure & Temps"
   Mobile-first · Noir profond · Or vieilli · Parchemin
   ----------------------------------------------------------- */

const NavLink = ({ to, children, testId, isHash }) => {
  const cls =
    "text-parchemin/70 hover:text-or transition-colors duration-300 text-sm tracking-wide";
  if (isHash) {
    return (
      <a href={to} data-testid={testId} className={cls}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} data-testid={testId} className={cls}>
      {children}
    </Link>
  );
};

const OrnamentDivider = ({ children }) => (
  <div className="divider-ornament font-display italic text-or/80 text-sm">
    <span aria-hidden="true">✦</span>
    {children && <span>{children}</span>}
    <span aria-hidden="true">✦</span>
  </div>
);

/* --- Hero avec photo réelle du Lot (cité médiévale au coucher de soleil) --- */
const Hero = () => (
  <section
    className="relative overflow-hidden min-h-[92vh] flex items-center"
    data-testid="landing-hero"
  >
    {/* Photo de fond responsive · <picture> pour srcset webp + fallback */}
    <picture>
      <source
        type="image/webp"
        media="(max-width: 640px)"
        srcSet={heroImage800}
      />
      <source
        type="image/webp"
        media="(max-width: 1200px)"
        srcSet={heroImage1200}
      />
      <source type="image/webp" srcSet={heroImage1537} />
      <img
        src={heroImageJpg}
        alt="Cité médiévale du Lot au coucher de soleil — tour ancienne et vallée dorée"
        className="absolute inset-0 w-full h-full object-cover hero-image-position"
        loading="eager"
        fetchPriority="high"
        decoding="async"
        data-testid="hero-image"
      />
    </picture>

    {/* Dégradés multi-couches pour lisibilité et ambiance "Aventure & Temps" */}
    {/* 1. Voile sombre général — assombrit ~35% l'ensemble */}
    <div
      className="absolute inset-0 bg-noir/30"
      aria-hidden="true"
    />
    {/* 2. Dégradé bas → haut (du noir vers transparent) pour ancrer le texte */}
    <div
      className="absolute inset-0 bg-gradient-to-t from-noir via-noir/70 via-40% to-transparent"
      aria-hidden="true"
    />
    {/* 3. Vignette latérale douce (vintage) */}
    <div
      className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_35%,rgba(12,10,8,0.65)_100%)]"
      aria-hidden="true"
    />
    {/* 4. Grain subtil */}
    <div className="absolute inset-0 bg-grain pointer-events-none" aria-hidden="true" />

    {/* Contenu texte — aligné bas-gauche pour respecter la photo */}
    <div className="relative z-10 w-full max-w-6xl mx-auto px-6 pt-24 pb-16 md:pt-32 md:pb-24">
      <div className="max-w-3xl">
        <div className="inline-flex items-center gap-2 px-3 py-1 mb-6 rounded-full border border-or/40 bg-noir/60 backdrop-blur-md animate-fade-up">
          <Compass className="w-3.5 h-3.5 text-or" />
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-or">
            Chapitre I · Le seuil
          </span>
        </div>

        <h1
          className="font-display font-medium text-[2.5rem] sm:text-5xl md:text-6xl lg:text-[5.5rem] leading-[1.02] tracking-tight text-parchemin animate-fade-up drop-shadow-[0_4px_24px_rgba(0,0,0,0.85)]"
          style={{ animationDelay: "0.05s", textWrap: "balance" }}
        >
          Et si votre ville
          <br />
          <span className="italic font-normal text-or drop-shadow-[0_2px_18px_rgba(0,0,0,0.9)]">
            cachait un secret&nbsp;?
          </span>
        </h1>

        <p
          className="mt-6 font-display italic text-lg sm:text-xl md:text-2xl text-parchemin/95 animate-fade-up drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)]"
          style={{ animationDelay: "0.12s" }}
        >
          Escape-game outdoor au cœur du{" "}
          <span className="text-or not-italic font-normal">Lot &amp; Célé</span>.
        </p>

        <p
          className="mt-5 max-w-xl text-sm sm:text-base leading-relaxed text-parchemin/80 animate-fade-up drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]"
          style={{ animationDelay: "0.2s" }}
        >
          Franchissez le seuil. Suivez la piste. Résolvez l&apos;énigme.
          Chaque rue, chaque pierre, chaque silence porte une trace de ceux qui
          vous ont précédé.
        </p>

        <div
          className="mt-10 flex flex-col sm:flex-row gap-3 animate-fade-up"
          style={{ animationDelay: "0.3s" }}
        >
          <a
            href="#portes"
            data-testid="cta-choisir-aventure"
            className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-or hover:bg-or-100 text-noir font-medium tracking-wide rounded-sm transition-all duration-300 shadow-[0_14px_50px_-12px_rgba(200,146,42,0.7)]"
          >
            Choisir mon aventure
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href="#comment-ca-marche"
            data-testid="cta-comment-ca-marche"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-parchemin/40 hover:border-or hover:bg-or/10 hover:text-or text-parchemin bg-noir/40 backdrop-blur-sm rounded-sm transition-all duration-300"
          >
            Comment ça marche
          </a>
        </div>

        {/* Coordonnées poétiques — vert profond pour l'ancrage terroir */}
        <div
          className="mt-14 inline-flex items-center gap-3 font-mono text-[10px] tracking-[0.3em] uppercase text-vert-50 animate-fade-up drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)]"
          style={{ animationDelay: "0.42s" }}
        >
          <span className="w-8 h-px bg-vert-50/60" />
          44°26′N &nbsp; · &nbsp; 1°26′E &nbsp; · &nbsp; Lot &amp; Célé
          <span className="w-8 h-px bg-vert-50/60" />
        </div>
      </div>
    </div>
  </section>
);

/* --- Deux portes : Picnic Game vs Escape Game --- */
const PorteCard = ({ variant, emoji, title, subtitle, features, cta, to, accent, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`group relative flex flex-col p-8 md:p-10 rounded-sm border transition-all duration-500 overflow-hidden ${
      variant === "picnic"
        ? "bg-gradient-to-br from-vert-900 to-noir border-vert-100/40 hover:border-vert-50/60"
        : "bg-gradient-to-br from-noir-50 to-noir border-or/25 hover:border-or/60"
    }`}
  >
    {/* Ornement d'angle */}
    <div className="absolute top-0 right-0 w-24 h-24 opacity-20 group-hover:opacity-40 transition-opacity duration-500">
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <path
          d="M0 0 L100 0 L100 30 M100 0 L70 0 L70 30 M85 0 L85 15"
          stroke={accent}
          strokeWidth="0.8"
          fill="none"
        />
        <circle cx="85" cy="15" r="2" fill={accent} />
      </svg>
    </div>

    <div className="relative flex items-start justify-between mb-8">
      <div
        className={`w-16 h-16 rounded-sm flex items-center justify-center text-3xl leading-none transition-transform duration-500 group-hover:scale-110 ${
          variant === "picnic"
            ? "bg-vert-50/10 ring-1 ring-vert-50/30"
            : "bg-or/10 ring-1 ring-or/30"
        }`}
        aria-hidden="true"
      >
        <span className="drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]">{emoji}</span>
      </div>
      <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-parchemin/40">
        {variant === "picnic" ? "Porte I" : "Porte II"}
      </div>
    </div>

    <div className="relative flex-1">
      <h3 className="font-display text-3xl md:text-4xl text-parchemin leading-tight">
        {title}
      </h3>
      <p className="mt-3 font-display italic text-lg text-or/80">{subtitle}</p>

      <ul className="mt-8 space-y-3">
        {features.map((f, i) => (
          <li
            key={i}
            className="flex items-start gap-3 text-sm text-parchemin/75"
          >
            <span
              className={`mt-1.5 w-1 h-1 rounded-full flex-shrink-0 ${
                variant === "picnic" ? "bg-vert-50" : "bg-or"
              }`}
            />
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </div>

    <div
      className={`relative mt-10 pt-6 border-t flex items-center justify-between ${
        variant === "picnic"
          ? "border-vert-100/30"
          : "border-or/20"
      }`}
    >
      <span
        className={`font-mono text-xs tracking-widest uppercase ${
          variant === "picnic" ? "text-vert-50" : "text-or"
        }`}
      >
        {cta}
      </span>
      <ArrowRight
        className={`w-5 h-5 group-hover:translate-x-1 transition-transform ${
          variant === "picnic" ? "text-vert-50" : "text-or"
        }`}
      />
    </div>
  </Link>
);

const PortesEntree = () => (
  <section
    id="portes"
    className="relative py-24 md:py-32 px-6 border-t border-or/10"
    data-testid="landing-portes"
  >
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-16 max-w-2xl mx-auto">
        <OrnamentDivider>Deux chemins</OrnamentDivider>
        <h2 className="mt-6 font-display text-3xl md:text-4xl lg:text-5xl text-parchemin leading-tight">
          Choisissez votre porte
          <br />
          <span className="italic text-or">d&apos;entrée</span>
        </h2>
        <p className="mt-5 text-parchemin/60 text-base">
          Deux expériences, deux ambiances. Une même envie : ralentir, regarder,
          comprendre ce que le lieu vous chuchote.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <PorteCard
          variant="picnic"
          emoji="🎒"
          title="Picnic Game"
          subtitle="Le chemin gourmand"
          features={[
            "25 cadenas physiques sur votre sac de picnic",
            "À chaque énigme résolue, un producteur local se dévoile",
            "Vin, fromages, noix, terrines — le terroir en récompense",
            "1h30 à 3h · idéal en couple, famille ou entre amis",
          ]}
          cta="Ouvrir la Porte I"
          to="/reserver?type=picnic"
          accent="#4A9B6E"
          testId="porte-picnic"
        />
        <PorteCard
          variant="escape"
          emoji="🔍"
          title="Escape Game"
          subtitle="Le chemin des ombres"
          features={[
            "Une trame narrative dense ancrée dans l'Histoire du lieu",
            "Énigmes de déduction, cryptographie, observation fine",
            "Chronomètre implacable — 90 minutes pour tout résoudre",
            "Défi corsé · groupes de 3 à 8 aventuriers",
          ]}
          cta="Ouvrir la Porte II"
          to="/reserver?type=escape"
          accent="#C8922A"
          testId="porte-escape"
        />
      </div>
    </div>
  </section>
);

/* --- Stats vivantes (placeholders honnêtes "—") --- */
const StatCard = ({ label, unit, hint, testId }) => (
  <div
    data-testid={testId}
    className="group relative flex flex-col items-start p-6 md:p-8 border border-or/15 hover:border-or/40 bg-noir-50/40 rounded-sm transition-all duration-500"
  >
    <div className="flex items-baseline gap-2">
      <span className="font-display text-5xl md:text-6xl text-or stat-graved leading-none">
        —
      </span>
      {unit && (
        <span className="font-mono text-xs text-or/60 uppercase tracking-widest">
          {unit}
        </span>
      )}
    </div>
    <div className="mt-4 text-parchemin/80 text-sm font-medium">{label}</div>
    {hint && (
      <div className="mt-1 text-parchemin/40 text-xs italic">{hint}</div>
    )}
  </div>
);

const StatsVivantes = () => (
  <section
    className="relative py-20 md:py-28 px-6 border-t border-or/10 bg-noir-100"
    data-testid="landing-stats"
  >
    <div className="max-w-6xl mx-auto">
      <div className="mb-12 max-w-2xl">
        <div className="inline-flex items-center gap-2 mb-4">
          <Sparkles className="w-3.5 h-3.5 text-or" />
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-or/70">
            Journal de bord
          </span>
        </div>
        <h2 className="font-display text-3xl md:text-4xl text-parchemin leading-tight">
          Une aventure qui s&apos;écrit
          <br />
          <span className="italic text-or">à mesure qu&apos;on la joue.</span>
        </h2>
        <p className="mt-4 text-parchemin/60 text-base">
          Les chiffres seront affichés dès l&apos;ouverture publique — ils se
          remplissent partie après partie.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Aventures accomplies"
          unit="parties"
          hint="depuis l'ouverture"
          testId="stat-parties"
        />
        <StatCard
          label="Énigmes résolues"
          unit="passages"
          hint="au fil des mois"
          testId="stat-enigmes"
        />
        <StatCard
          label="Villes explorées"
          unit="lieux"
          hint="en France"
          testId="stat-villes"
        />
        <StatCard
          label="Producteurs révélés"
          unit="artisans"
          hint="terroir local"
          testId="stat-partenaires"
        />
      </div>
    </div>
  </section>
);

/* --- Comment ça marche (mini rassurance) — patrimoine & nature (vert profond) --- */
const CommentCaMarche = () => {
  const steps = [
    {
      icon: MapPin,
      title: "Réservez un lieu",
      text: "Choisissez votre ville, votre date, votre porte d'entrée.",
    },
    {
      icon: Clock,
      title: "Récupérez votre sac",
      text: "À l'heure dite, sur place — sac scellé, énigmes prêtes.",
    },
    {
      icon: Users,
      title: "Jouez, marchez, savourez",
      text: "Le lieu devient votre décor. Le temps ralentit.",
    },
  ];

  return (
    <section
      id="comment-ca-marche"
      className="relative py-20 md:py-28 px-6 border-t border-or/10 bg-gradient-to-b from-noir via-vert-900 to-noir overflow-hidden"
      data-testid="landing-comment"
    >
      {/* Feuillage stylisé en filigrane (vert profond, patrimoine) */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        aria-hidden="true"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 400'><g fill='none' stroke='%234A9B6E' stroke-width='0.8'><path d='M60 340 Q 80 200 140 120 Q 100 130 80 160 M140 120 Q 160 100 200 90 Q 180 110 170 130 M200 90 Q 240 100 260 130 Q 250 110 260 100'/><path d='M340 340 Q 320 200 260 120 Q 300 130 320 160 M260 120 Q 240 100 200 90'/><path d='M200 340 L200 100' stroke-width='1.2'/><ellipse cx='170' cy='180' rx='18' ry='6' transform='rotate(-30 170 180)'/><ellipse cx='230' cy='180' rx='18' ry='6' transform='rotate(30 230 180)'/><ellipse cx='160' cy='230' rx='22' ry='7' transform='rotate(-20 160 230)'/><ellipse cx='240' cy='230' rx='22' ry='7' transform='rotate(20 240 230)'/><ellipse cx='150' cy='280' rx='26' ry='8' transform='rotate(-15 150 280)'/><ellipse cx='250' cy='280' rx='26' ry='8' transform='rotate(15 250 280)'/></g></svg>\")",
          backgroundRepeat: "repeat",
          backgroundSize: "320px auto",
        }}
      />

      <div className="relative max-w-5xl mx-auto">
        <div className="text-center mb-14 max-w-xl mx-auto">
          <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full border border-vert-50/30 bg-vert-900/40 backdrop-blur-sm">
            <Leaf className="w-3.5 h-3.5 text-vert-50" />
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-vert-50">
              Patrimoine &amp; nature
            </span>
          </div>
          <h2 className="mt-2 font-display text-3xl md:text-4xl text-parchemin leading-tight">
            Trois pas pour
            <span className="italic text-or"> franchir le seuil</span>
          </h2>
          <p className="mt-4 text-parchemin/60 text-sm">
            Un rituel simple, ancré dans le lieu — dehors, hors les murs, hors du temps.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {steps.map((s, i) => (
            <div
              key={i}
              className="relative flex flex-col items-start p-6 pl-7 border-l-2 border-vert-50/40 hover:border-vert-50 bg-vert-900/30 hover:bg-vert-900/50 backdrop-blur-sm rounded-r-sm transition-all duration-500"
              data-testid={`step-${i + 1}`}
            >
              <span className="font-mono text-[10px] tracking-[0.3em] text-vert-50 mb-4">
                {String(i + 1).padStart(2, "0")} · ÉTAPE
              </span>
              <s.icon
                className="w-6 h-6 text-or mb-4"
                strokeWidth={1.5}
              />
              <h3 className="font-display text-xl text-parchemin mb-2">
                {s.title}
              </h3>
              <p className="text-sm text-parchemin/70 leading-relaxed">
                {s.text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* --- Espace B2B avec 3 cartes --- */
const B2BCard = ({ icon: Icon, title, text, cta, testId }) => (
  <div
    className="group flex flex-col p-7 border border-or/15 hover:border-or/45 bg-noir-50/50 rounded-sm transition-all duration-500"
    data-testid={testId}
  >
    <div className="w-11 h-11 rounded-sm bg-or/10 text-or flex items-center justify-center mb-6">
      <Icon className="w-5 h-5" strokeWidth={1.5} />
    </div>
    <h3 className="font-display text-xl text-parchemin mb-3">{title}</h3>
    <p className="text-sm text-parchemin/65 leading-relaxed flex-1">{text}</p>
    <div className="mt-6 pt-4 border-t border-or/15 text-or font-mono text-xs tracking-widest uppercase group-hover:tracking-[0.2em] transition-all">
      {cta}
    </div>
  </div>
);

const EspaceB2B = () => (
  <section
    id="b2b"
    className="relative py-24 md:py-32 px-6 border-t border-or/10 bg-gradient-to-b from-noir to-vert-900/30"
    data-testid="landing-b2b"
  >
    <div className="max-w-6xl mx-auto">
      <div className="mb-14 max-w-3xl">
        <div className="inline-flex items-center gap-2 mb-4">
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-or">
            Espace professionnel
          </span>
        </div>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-parchemin leading-tight">
          Faire de votre lieu
          <br />
          <span className="italic text-or">une aventure inoubliable.</span>
        </h2>
        <p className="mt-5 text-parchemin/60 text-base max-w-2xl">
          Nous concevons des expériences narratives sur mesure — enracinées dans
          l&apos;Histoire, le terroir et l&apos;âme du lieu. Pour les acteurs qui veulent
          offrir plus qu&apos;une visite : un souvenir.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <B2BCard
          icon={Landmark}
          title="Offices de tourisme"
          text="Attirez de nouveaux publics, allongez la durée des séjours, mettez en avant vos producteurs. Une aventure, une carte postale vivante de votre territoire."
          cta="En parler ensemble"
          testId="b2b-tourisme"
        />
        <B2BCard
          icon={Building2}
          title="Mairies & Communes"
          text="Réanimez le centre-bourg, révélez le patrimoine invisible, fédérez habitants et visiteurs autour d'un récit commun ancré dans la mémoire locale."
          cta="Étudier votre projet"
          testId="b2b-mairies"
        />
        <B2BCard
          icon={Briefcase}
          title="Entreprises & Séminaires"
          text="Un team-building qui ne ressemble à rien de connu. Marcher, résoudre, savourer ensemble — hors des murs, hors du script, dans le vrai."
          cta="Concevoir un séminaire"
          testId="b2b-entreprises"
        />
      </div>

      <div className="mt-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6 p-8 border-or-vieilli rounded-sm bg-noir-100/60">
        <div>
          <div className="font-display text-2xl text-parchemin">
            Parlons de votre lieu
          </div>
          <p className="mt-2 text-sm text-parchemin/60 max-w-md">
            Devis, cahier des charges, rencontre sur site — nous répondons sous
            48h ouvrées.
          </p>
        </div>
        <a
          href="mailto:contact@picnicgame.fr?subject=Projet%20B2B%20-%20Picnic-Game"
          data-testid="b2b-cta-contact"
          className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-or hover:bg-or-100 text-noir font-medium tracking-wide rounded-sm transition-all duration-300 shadow-[0_10px_40px_-10px_rgba(200,146,42,0.5)]"
        >
          <Mail className="w-4 h-4" />
          contact@picnicgame.fr
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </a>
      </div>
    </div>
  </section>
);

/* --- Header sticky sobre --- */
const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-500 ${
        scrolled
          ? "bg-noir/85 backdrop-blur-md border-b border-or/15"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="landing-header"
    >
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-2 group"
          data-testid="nav-logo"
        >
          <Compass className="w-5 h-5 text-or group-hover:rotate-45 transition-transform duration-500" />
          <span className="font-display text-lg text-parchemin tracking-wide">
            Picnic<span className="text-or">·</span>Game
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="#portes" testId="nav-portes" isHash>
            Aventures
          </NavLink>
          <NavLink to="#comment-ca-marche" testId="nav-comment" isHash>
            Le rituel
          </NavLink>
          <NavLink to="#b2b" testId="nav-b2b" isHash>
            Pros
          </NavLink>
          <NavLink to="/galerie" testId="nav-galerie">
            Galerie
          </NavLink>
        </nav>
        <EspaceMenu />
      </div>
    </header>
  );
};

/* --- Footer discret --- */
const Footer = () => (
  <footer
    className="border-t border-or/15 bg-noir-100 py-12 px-6"
    data-testid="landing-footer"
  >
    <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 text-parchemin/50 text-xs">
      <div className="flex items-center gap-2">
        <Compass className="w-3.5 h-3.5 text-or/70" />
        <span className="font-display tracking-wide">
          Picnic<span className="text-or">·</span>Game — Lot &amp; Célé
        </span>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
        <Link
          to="/legal/mentions-legales"
          className="hover:text-or transition-colors"
          data-testid="footer-mentions"
        >
          Mentions légales
        </Link>
        <Link
          to="/legal/cgv"
          className="hover:text-or transition-colors"
          data-testid="footer-cgv"
        >
          CGV
        </Link>
        <Link
          to="/legal/confidentialite"
          className="hover:text-or transition-colors"
          data-testid="footer-conf"
        >
          Confidentialité
        </Link>
        <a
          href="mailto:contact@picnicgame.fr"
          className="hover:text-or transition-colors"
          data-testid="footer-contact"
        >
          contact@picnicgame.fr
        </a>
      </div>
      <div className="font-mono text-[10px] tracking-widest uppercase text-or/40">
        MMXXVI
      </div>
    </div>
  </footer>
);

/* --- Page --- */
export default function Landing() {
  // On garde le fetch parcours pour ne rien casser côté API, même si non affiché ici
  const fetched = useRef(false);
  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    publicEndpoints.parcours().catch(() => {});
  }, []);

  return (
    <div
      className="min-h-screen bg-noir text-parchemin bg-grain"
      data-testid="landing-page"
    >
      <Header />
      <main>
        <Hero />
        <PortesEntree />
        <CommentCaMarche />
        <StatsVivantes />
        <EspaceB2B />
      </main>
      <Footer />
    </div>
  );
}
